import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Slide,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Today as TodayIcon,
  Close as CloseIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';
import { mobileApi, mobileRequest } from '../../../services/mobile/mobileApi';
import { formatCurrencyAmount } from '../../../utils/currencyUtils';

// ─── Constants ───────────────────────────────────────────

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const STATUS_COLORS = {
  Pending:   { bg: '#fef3c7', color: '#d97706', dot: '#f59e0b' },
  Printed:   { bg: '#ede9fe', color: '#7c3aed', dot: '#8b5cf6' },
  Collected: { bg: '#dbeafe', color: '#2563eb', dot: '#3b82f6' },
  Cleared:   { bg: '#d1fae5', color: '#059669', dot: '#10b981' },
  Cancelled: { bg: '#fee2e2', color: '#dc2626', dot: '#ef4444' },
  Bounced:   { bg: '#fce7f3', color: '#be185d', dot: '#ec4899' },
};

const VISIBLE_STATUSES = ['Pending', 'Printed', 'Collected', 'Cleared'];

// ─── Helpers ─────────────────────────────────────────────

const getCalendarDays = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const startDate = new Date(firstDay);
  startDate.setDate(startDate.getDate() - firstDay.getDay());

  const days = [];
  const current = new Date(startDate);
  for (let i = 0; i < 42; i++) {
    days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return days;
};

const dateKey = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const isDateToday = (d) => {
  const today = new Date();
  return d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
};

const formatAmount = (amount, currency = 'AED') => formatCurrencyAmount(amount, currency);

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString();
  } catch {
    return dateStr;
  }
};

const DialogTransition = (props) => <Slide direction="up" {...props} />;

// ─── Component ───────────────────────────────────────────

const MobilePDCCalendar = () => {
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [cheques, setCheques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [banks, setBanks] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  // Load banks
  useEffect(() => {
    const loadBanks = async () => {
      try {
        const response = await mobileApi.get('/banks');
        const responseData = response.data;
        const data = responseData?.Data || responseData?.data || responseData || [];
        setBanks(Array.isArray(data) ? data : []);
      } catch {
        // Silent fail
      }
    };
    loadBanks();
  }, []);

  const loadCheques = useCallback(async () => {
    setLoading(true);
    try {
      const result = await mobileRequest(() =>
        mobileApi.get('/cheques', { params: { pageNumber: 1, pageSize: 10000, status: '' } })
      );
      if (result.success && result.data) {
        setCheques(Array.isArray(result.data) ? result.data : []);
      } else {
        setCheques([]);
      }
    } catch {
      setCheques([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCheques();
  }, [loadCheques]);

  // Group cheques by date (exclude Cancelled & Bounced)
  const chequesByDate = useMemo(() => {
    const map = {};
    cheques.forEach((c) => {
      const status = c.Status || c.status || 'Pending';
      if (!VISIBLE_STATUSES.includes(status)) return;
      if (selectedStatus !== 'all' && status !== selectedStatus) return;

      if (selectedBankId !== 'all') {
        const bankId = c.BankId || c.bankId;
        if (String(bankId) !== String(selectedBankId)) return;
      }

      const raw = c.ChqDate || c.chqDate;
      if (!raw) return;
      const d = new Date(raw);
      if (isNaN(d.getTime())) return;
      const key = dateKey(d);
      if (!map[key]) map[key] = [];
      map[key].push({
        Id: c.Id || c.id,
        ChequeNumber: c.ChequeNumber || c.chequeNumber || '',
        BeneficiaryName: c.BeneficiaryName || c.beneficiaryName || '',
        ChqAmount: c.ChqAmount || c.chqAmount || 0,
        ChqDate: raw,
        Status: status,
        BankName: c.BankName || c.bankName || '',
        Currency: c.Currency || c.currency || 'AED',
      });
    });
    return map;
  }, [cheques, selectedBankId, selectedStatus]);

  // Month stats
  const monthStats = useMemo(() => {
    let count = 0, total = 0, pending = 0, printed = 0;
    Object.entries(chequesByDate).forEach(([key, dayCheques]) => {
      const d = new Date(key);
      if (d.getMonth() !== currentMonth || d.getFullYear() !== currentYear) return;
      dayCheques.forEach((c) => {
        count++;
        total += Number(c.ChqAmount || 0);
        if (c.Status === 'Pending') pending++;
        else if (c.Status === 'Printed') printed++;
      });
    });
    return { count, total, pending, printed };
  }, [chequesByDate, currentMonth, currentYear]);

  const calendarDays = useMemo(
    () => getCalendarDays(currentYear, currentMonth),
    [currentYear, currentMonth]
  );

  const goToToday = () => {
    setCurrentYear(today.getFullYear());
    setCurrentMonth(today.getMonth());
  };

  const goPrev = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear((y) => y - 1); }
    else setCurrentMonth((m) => m - 1);
  };

  const goNext = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear((y) => y + 1); }
    else setCurrentMonth((m) => m + 1);
  };

  const handleDayClick = (day) => {
    const key = dateKey(day);
    if (chequesByDate[key]?.length > 0) {
      setSelectedDate(day);
    }
  };

  const selectedCheques = selectedDate ? (chequesByDate[dateKey(selectedDate)] || []) : [];
  const selectedTotal = selectedCheques.reduce((sum, c) => sum + Number(c.ChqAmount || 0), 0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress sx={{ color: '#2563eb' }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
        PDC Calendar
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <FormControl size="small" sx={{ flex: 1 }}>
          <Select
            value={selectedBankId}
            onChange={(e) => setSelectedBankId(e.target.value)}
            displayEmpty
            sx={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              fontSize: '0.8rem',
              fontWeight: 600,
              '& .MuiSelect-select': { py: 0.75, pl: 1.5 },
            }}
          >
            <MenuItem value="all">All Banks</MenuItem>
            {banks.map((bank) => {
              const id = bank.Id || bank.id;
              const name = bank.AccountName || bank.accountName || '';
              const currency = bank.Currency || bank.currency || '';
              return (
                <MenuItem key={id} value={id}>
                  {name} ({currency})
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            displayEmpty
            sx={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              fontSize: '0.8rem',
              fontWeight: 600,
              '& .MuiSelect-select': { py: 0.75, pl: 1.5 },
            }}
          >
            <MenuItem value="all">All Statuses</MenuItem>
            {VISIBLE_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircleIcon sx={{ fontSize: 7, color: STATUS_COLORS[s].dot }} />
                  {s}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Month stats */}
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
        <Chip label={`${monthStats.count} cheques`} size="small" sx={{ backgroundColor: '#eff6ff', color: '#2563eb', fontWeight: 600, fontSize: '0.7rem' }} />
        <Chip label={formatAmount(monthStats.total)} size="small" sx={{ backgroundColor: '#f0fdf4', color: '#059669', fontWeight: 600, fontSize: '0.7rem' }} />
        {monthStats.pending > 0 && (
          <Chip label={`${monthStats.pending} pending`} size="small" sx={{ backgroundColor: STATUS_COLORS.Pending.bg, color: STATUS_COLORS.Pending.color, fontWeight: 600, fontSize: '0.65rem', height: 22 }} />
        )}
      </Box>

      {/* Calendar Card */}
      <Box sx={{ backgroundColor: '#fff', borderRadius: 3, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        {/* Month navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, borderBottom: '1px solid #e2e8f0' }}>
          <IconButton onClick={goPrev} size="small" sx={{ color: '#64748b' }}>
            <ChevronLeft />
          </IconButton>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
            {MONTH_NAMES[currentMonth]} {currentYear}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton onClick={goToToday} size="small" sx={{ color: '#2563eb' }}>
              <TodayIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={goNext} size="small" sx={{ color: '#64748b' }}>
              <ChevronRight />
            </IconButton>
          </Box>
        </Box>

        {/* Weekday headers */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid #e2e8f0' }}>
          {WEEKDAYS.map((day, i) => (
            <Typography
              key={i}
              sx={{
                textAlign: 'center',
                py: 0.75,
                fontSize: '0.7rem',
                fontWeight: 700,
                color: i === 5 ? '#dc2626' : '#64748b',
                textTransform: 'uppercase',
              }}
            >
              {day}
            </Typography>
          ))}
        </Box>

        {/* Calendar grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {calendarDays.map((day, idx) => {
            const key = dateKey(day);
            const dayCheques = chequesByDate[key] || [];
            const isCurrentMonth = day.getMonth() === currentMonth;
            const isFriday = day.getDay() === 5;
            const isTodayDate = isDateToday(day);
            const hasCheques = dayCheques.length > 0 && isCurrentMonth;

            return (
              <Box
                key={idx}
                onClick={() => isCurrentMonth && handleDayClick(day)}
                sx={{
                  position: 'relative',
                  aspectRatio: '1',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: hasCheques ? 'pointer' : 'default',
                  opacity: isCurrentMonth ? 1 : 0.3,
                  backgroundColor: isTodayDate ? '#eff6ff' : 'transparent',
                  '&:active': hasCheques ? { backgroundColor: '#dbeafe' } : {},
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.82rem',
                    fontWeight: isTodayDate ? 800 : 500,
                    color: isTodayDate ? '#fff' : isFriday ? '#dc2626' : '#0f172a',
                    width: isTodayDate ? 26 : 'auto',
                    height: isTodayDate ? 26 : 'auto',
                    borderRadius: '50%',
                    backgroundColor: isTodayDate ? '#2563eb' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                  }}
                >
                  {day.getDate()}
                </Typography>
                {/* Dot indicators for cheques */}
                {hasCheques && (
                  <Box sx={{ display: 'flex', gap: '2px', mt: '2px' }}>
                    {dayCheques.length <= 3 ? (
                      dayCheques.map((c, i) => {
                        const colors = STATUS_COLORS[c.Status] || STATUS_COLORS.Pending;
                        return <CircleIcon key={i} sx={{ fontSize: 5, color: colors.dot }} />;
                      })
                    ) : (
                      <>
                        <CircleIcon sx={{ fontSize: 5, color: '#2563eb' }} />
                        <Typography sx={{ fontSize: '0.55rem', color: '#2563eb', fontWeight: 700, lineHeight: 1 }}>
                          {dayCheques.length}
                        </Typography>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Status legend */}
        <Box sx={{ display: 'flex', gap: 1.5, px: 1.5, py: 1, borderTop: '1px solid #e2e8f0', justifyContent: 'center' }}>
          {Object.entries(STATUS_COLORS).filter(([s]) => s !== 'Cancelled' && s !== 'Bounced').map(([status, colors]) => (
            <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <CircleIcon sx={{ fontSize: 6, color: colors.dot }} />
              <Typography sx={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 500 }}>{status}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Day Detail Dialog (fullscreen on mobile) */}
      <Dialog
        open={!!selectedDate}
        onClose={() => setSelectedDate(null)}
        fullScreen
        TransitionComponent={DialogTransition}
        PaperProps={{ sx: { backgroundColor: '#f8fafc' } }}
      >
        {selectedDate && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, px: 2, backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0' }}>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a' }}>
                  {selectedDate.toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>
                  {selectedCheques.length} cheque{selectedCheques.length !== 1 ? 's' : ''} &mdash; {formatAmount(selectedTotal)}
                </Typography>
              </Box>
              <IconButton onClick={() => setSelectedDate(null)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              {selectedCheques.map((cheque, i) => {
                const colors = STATUS_COLORS[cheque.Status] || STATUS_COLORS.Pending;
                return (
                  <Box key={i}>
                    <Box sx={{ px: 2, py: 1.5, backgroundColor: '#fff' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {cheque.BeneficiaryName}
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mt: 0.25 }}>
                            Chq# {cheque.ChequeNumber}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 1 }}>
                          <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f172a', fontFamily: 'monospace' }}>
                            {formatAmount(cheque.ChqAmount, cheque.Currency)}
                          </Typography>
                          <Chip
                            label={cheque.Status}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              backgroundColor: colors.bg,
                              color: colors.color,
                              mt: 0.5,
                            }}
                          />
                        </Box>
                      </Box>
                      <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mt: 0.5 }}>
                        {cheque.BankName} &bull; {formatDate(cheque.ChqDate)}
                      </Typography>
                    </Box>
                    {i < selectedCheques.length - 1 && <Divider />}
                  </Box>
                );
              })}

              {/* Total row */}
              <Box sx={{ px: 2, py: 1.5, mt: 1, mx: 2, backgroundColor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>Total</Typography>
                  <Typography sx={{ fontWeight: 800, fontFamily: 'monospace', color: '#0f172a' }}>
                    {formatAmount(selectedTotal)}
                  </Typography>
                </Box>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default MobilePDCCalendar;
